import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api, saveBlob } from '../api'
import { formatDate } from '../lib/format'
import type { EstimateLineDraft, Task } from '../types'
import { useEscClose } from '../lib/useEscClose'

interface Props {
  task: Task
  /** Согласие администратора править задачу, попавшую в ведомость. */
  confirmLocked?: boolean
  onClose: () => void
}

function toDrafts(task: Task): EstimateLineDraft[] {
  if (task.estimate_lines.length === 0) return [{ title: '', hours: '' }]
  return task.estimate_lines.map((l) => ({ title: l.title, hours: String(Number(l.hours)) }))
}

/** Окно оценки: построчно наименование работ и часы, итог — их сумма. */
export default function EstimateDialog({ task, confirmLocked = false, onClose }: Props) {
  const qc = useQueryClient()
  const [lines, setLines] = useState<EstimateLineDraft[]>(() => toDrafts(task))
  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const filled = useMemo(
    () => lines.filter((l) => l.title.trim() !== '' || l.hours.trim() !== ''),
    [lines],
  )

  const total = useMemo(
    () => filled.reduce((sum, l) => sum + (Number(l.hours) || 0), 0),
    [filled],
  )

  const hadEstimate = task.estimate !== null && task.estimate !== undefined
  const changed = Number(task.estimate ?? 0) !== total || !hadEstimate

  const save = useMutation({
    mutationFn: () =>
      api.setEstimate(
        task.id,
        filled.map((l) => ({ title: l.title.trim(), hours: Number(l.hours) || 0 })),
        hadEstimate ? comment : undefined,
        confirmLocked,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['kpi'] })
      onClose()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить оценку'),
  })

  function patchLine(index: number, patch: Partial<EstimateLineDraft>) {
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)))
  }

  /* Смету выгружают и до сохранения — её обсуждают с клиентом, а закрепляют
     в задаче уже после. Поэтому всё ниже берёт строки с экрана. */

  const exportLines = () =>
    filled.map((l) => ({ title: l.title.trim(), hours: Number(l.hours) || 0 }))

  const fileBase = `Оценка_задача_${task.id}_${new Date().toISOString().slice(0, 10)}`

  /** Текст сметы: в него же уходит и копирование, и файл для блокнота. */
  function asText(): string {
    const head = [
      `Оценка работ по задаче № ${task.id}`,
      task.client_name ? `Клиент: ${task.client_name}` : '',
      task.subclient_name ? `Объект: ${task.subclient_name}` : '',
      `Тема: ${task.title}`,
      `Дата: ${formatDate(task.work_date)}`,
    ].filter(Boolean)

    // Ширину столбца считаем по самой длинной строке: в блокноте нет таблиц,
    // и без выравнивания часы расползаются по всей странице.
    const width = Math.max(20, ...filled.map((l) => l.title.trim().length))
    const rows = filled.map(
      (l) => `${l.title.trim().padEnd(width)}  ${String(Number(l.hours) || 0).padStart(6)} ч`,
    )
    const ruler = '-'.repeat(width + 9)

    return [
      ...head,
      '',
      `${'Наименование работ'.padEnd(width)}  ${'Часы'.padStart(6)}`,
      ruler,
      ...rows,
      ruler,
      `${'ИТОГО'.padEnd(width)}  ${String(total).padStart(6)} ч`,
      '',
    ].join('\r\n')
  }

  async function copyAll() {
    setError('')
    try {
      await navigator.clipboard.writeText(asText())
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Браузер не дал доступ к буферу обмена')
    }
  }

  function saveTxt() {
    // Windows-1251 не нужен: блокнот давно открывает UTF-8, а метка в начале
    // файла не даёт ему принять кириллицу за кодировку системы.
    saveBlob(new Blob(['\uFEFF' + asText()], { type: 'text/plain;charset=utf-8' }),
             `${fileBase}.txt`)
  }

  async function saveXlsx() {
    setError('')
    try {
      await api.exportEstimate(task.id, exportLines(), `${fileBase}.xlsx`)
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось сформировать файл')
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (filled.some((l) => !l.title.trim())) {
      return setError('У каждой строки должно быть наименование работ')
    }
    if (filled.some((l) => Number(l.hours) < 0 || Number.isNaN(Number(l.hours)))) {
      return setError('Часы должны быть неотрицательным числом')
    }
    save.mutate()
  }

  useEscClose(onClose)

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" onSubmit={submit} style={{ maxWidth: 720 }}>
        <h2>Оценка задачи «{task.title}»</h2>

        <div className="modal-body">
          {error && <div className="error">{error}</div>}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: '60%' }}>Наименование работ</th>
                <th className="num" style={{ width: 120 }}>
                  Часы
                </th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td>
                    <input
                      value={line.title}
                      onChange={(e) => patchLine(i, { title: e.target.value })}
                      placeholder="Настройка сканера"
                      style={{ width: '100%' }}
                      autoFocus={i === 0}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      value={line.hours}
                      onChange={(e) => patchLine(i, { hours: e.target.value })}
                      style={{ width: '100%', textAlign: 'right' }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn small danger"
                      title="Удалить строку"
                      onClick={() =>
                        setLines(
                          lines.length === 1
                            ? [{ title: '', hours: '' }]
                            : lines.filter((_, j) => j !== i),
                        )
                      }
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700 }}>
                <td>ИТОГО ОЦЕНКА</td>
                <td className="num mono">{total ? `${total} ч` : '—'}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>

        <div className="estimate-tools">
          <button
            type="button"
            className="btn small"
            onClick={() => setLines([...lines, { title: '', hours: '' }])}
          >
            + Строка
          </button>

          <div className="estimate-tools-right">
            <button type="button" className="btn small" disabled={filled.length === 0} onClick={copyAll}>
              {copied ? '✓ Скопировано' : 'Копировать'}
            </button>
            <button type="button" className="btn small" disabled={filled.length === 0} onClick={saveTxt}>
              В блокнот
            </button>
            <button
              type="button"
              className="btn small"
              disabled={filled.length === 0}
              onClick={() => void saveXlsx()}
            >
              В Excel
            </button>
          </div>
        </div>

        {hadEstimate && changed && (
          <div className="field full highlight" style={{ marginTop: 14 }}>
            <label>Причина изменения оценки</label>
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="клиент добавил вторую точку"
            />
            <span className="hint-inline">
              Изменение с {Number(task.estimate)} на {total || '—'} попадёт в историю задачи
              вместе с составом работ.
            </span>
          </div>
        )}

          {filled.length === 0 && hadEstimate && (
            <p style={{ color: 'var(--muted)' }}>
              Все строки пусты — оценка будет снята, задача снова попадёт в «Без оценки».
            </p>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить оценку'}
          </button>
        </div>
      </form>
    </div>
  )
}
